import styles from './OrderEconomicsPanel.module.css';

/**
 * The shared presentational parts of the order economics panel — card 2459.
 *
 * 🔑 WHY THIS FILE EXISTS. `OrderEconomicsPanel` and `OrderFirstOrderValueSection` render the
 * SAME row: label, amount, quality badge, provenance line. Two copies of that markup would be
 * free to drift, which is the exact failure the panel's own header comment warns about for its
 * two mounts. One definition, imported twice, cannot.
 *
 * 🪤 It imports the PANEL's stylesheet on purpose. These are the panel's parts, not a component
 * with a life of its own, and a CSS module imported from two files yields the same hashed class
 * names — so the rows are styled identically by construction rather than by a copied rule.
 *
 * 🔴 PURELY PRESENTATIONAL — IT DERIVES NO MONEY. Formatting only; every figure arrives already
 * computed and stored. `formatDollars` returns null for absence so a caller renders a WORD, and
 * `$0.00` can never stand in for "no row" — a zero and an un-recorded line are different facts.
 */

export const ABSENT_LABEL = 'Not recorded';

/** The canonical stored lines, in the order card 2257 names them. `sourceKey` indexes the raw
 *  `line_sources` jsonb, which is deliberately never case-transformed and stays snake_case.
 *  Shared, so the full-term block and the metric block can never present a different ORDER of
 *  the same lines — the metric derives its own list from this one by subtraction. */
export const ECONOMICS_LINES = Object.freeze([
  { key: 'grossRevenue', sourceKey: 'gross_revenue', label: 'Gross charged' },
  { key: 'reserveWithheld', sourceKey: 'reserve_withheld', label: 'Reserve withheld (10%)' },
  { key: 'netRevenue', sourceKey: 'net_revenue', label: 'Net revenue', emphasis: true },
  { key: 'medicationCost', sourceKey: 'medication_cost', label: 'Medication' },
  { key: 'shippingCost', sourceKey: 'shipping_cost', label: 'Shipping' },
  { key: 'ancillaryCost', sourceKey: 'ancillary_cost', label: 'Ancillary' },
  { key: 'consultationCost', sourceKey: 'consultation_cost', label: 'Consultation' },
  { key: 'providerFee', sourceKey: 'provider_fee', label: 'Provider fee' },
  { key: 'totalExpense', sourceKey: 'total_expense', label: 'Total expense' },
  { key: 'profit', sourceKey: 'profit', label: 'Profit', emphasis: true }
]);

export const isAbsent = (value) => value === null || value === undefined || value === '';

/** Stored dollars -> a money string. Returns null for absence so the caller renders a word,
 *  never `$0.00` — a zero and an un-recorded line are different facts. */
export const formatDollars = (value) => (isAbsent(value) ? null : `$${Number(value).toFixed(2)}`);

/** `margin` is a ratio in `numeric(9,4)`, NOT money, and is NULL when net revenue is 0. */
export const formatMargin = (value) => (isAbsent(value) ? null : `${(Number(value) * 100).toFixed(2)}%`);

/** `source · reason` from the stored provenance blob, or null when the line has none. */
export const formatLineSource = (line) => {
  if (!line) return null;
  const parts = [line.source, line.reason].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

/** Profit is the only line that earns a colour, and only when it is actually present. */
export const deriveProfitTone = (amount) =>
  (isAbsent(amount) ? null : (Number(amount) < 0 ? 'amountNegative' : 'amountPositive'));

export const QualityBadge = ({ quality }) => {
  if (isAbsent(quality)) return null;
  const verified = quality === 'verified';
  return (
    <span
      className={`${styles.badge} ${verified ? styles.badgeVerified : styles.badgeEstimated}`}
      data-quality={quality}
    >
      {verified ? 'Verified' : 'Estimated'}
    </span>
  );
};

export const EconomicsRow = ({ testId, label, amount, quality, source, emphasis, tone }) => (
  <div
    className={emphasis ? `${styles.row} ${styles.rowEmphasis}` : styles.row}
    data-economics-line={testId}
  >
    <span className={styles.label}>{label}</span>
    <span className={styles.valueCell}>
      <span className={styles.amountLine}>
        {isAbsent(amount)
          ? <span className={styles.absent} data-absent="true">{ABSENT_LABEL}</span>
          : <span className={`${styles.amount} ${tone ? styles[tone] : ''}`} data-amount={amount}>{amount}</span>}
        <QualityBadge quality={quality} />
      </span>
      {source && <span className={styles.source}>{source}</span>}
    </span>
  </div>
);

export default EconomicsRow;
